import gsap from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import {SplitText} from 'gsap/SplitText'
import LazyLoad from 'vanilla-lazyload'
import barba from '@barba/core'
import Lenis from 'lenis'
import mitt from 'mitt'

import Site from 'stores/Site'

import Cursor from 'components/Cursor'
import Drawer from 'components/Drawer'
import Header from 'components/Header'
import {remToPx} from "./utils/remToPx";

class App {
    constructor() {
        this.setScrollRestoration()
        gsap.registerPlugin(ScrollTrigger)
        gsap.registerPlugin(SplitText)
        gsap.ticker.lagSmoothing(0)
        ScrollTrigger.config({ ignoreMobileResize: true})

        this.classes = []
        this.pendingHash = null

        Site.isFirstLoad = true
        Site.pageClasses = {
            default: 'Default',
            home: 'Home',
            post: 'Post',
            legal: 'Legal'
        }

        document.addEventListener('DOMContentLoaded', () => {
            this.init()
        })
    }

    setScrollRestoration() {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual'
        }
    }

    init() {
        Site.windowWidth = window.innerWidth
        Site.html = document.documentElement
        Site.body = document.querySelector('body')
        Site.page = document.querySelector('.main')
        Site.pageLoader = document.querySelector('.page-loader')
        Site.ctx = this.ctx.bind(this)
        Site.emitter = mitt()
        Site.resize = () => this.resize()
        Site.prm = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        this.checkMobile()

        Site.platformOnLoad = Site.isMobile ? 'mobile' : 'desktop'

        this.initLazy()
        this.initScroll()
        this.initHeader()
        this.addEvents()
        this.initDrawer()
        this.initCursor()
        this.initLifeCycle()
    }

    initLazy() {
        Site.lazy = new LazyLoad({
            threshold: 1000,
            unobserve_entered: true,
            callback_loaded: (el) => {
                el.closest('.media-wrapper').classList.remove('loading')
                el.closest('.media-wrapper').classList.add('loaded')
            }
        })
    }

    initHeader() {
        Site.header = new Header()
        Site.header.init()

        this.classes.push(Site.header)
    }

    initLifeCycle() {
        this.transitions = {
            name: 'default',
            once() {},
            leave() {},
            beforeEnter() {},
            enter() {},
        }
        this.transitions.once.bind(this)
        this.transitions.leave.bind(this)
        this.transitions.beforeEnter.bind(this)
        this.transitions.enter.bind(this)

        this.transitions.once = async (data) => {
            window.scroll(0, 0)
            Site.header.updateHeader(data)

            await this.initPage()

            Site.scroll.start()
            Site.pageClass.pageLoaded()

            this.scrollToHash(window.location.hash)
        }

        this.transitions.leave = async (data) => {
            if (Site.navActive) {
                Site.header.hide()
            }

            Site.isFirstLoad = false
            Site.scroll.stop()
            Site.html.classList.add('is-transitioning')

            await new Promise(resolve => {
                this.inTl && this.inTl.kill()

                this.inTl = gsap.timeline({
                    onComplete: resolve
                })

                this.inTl.set(Site.html, {
                    cursor: 'wait'
                })

                this.inTl.to(Site.pageLoader, {
                    autoAlpha: 1,
                    duration: .5,
                    ease: 'power1.inOut'
                })
            })

            Site.pageClass && Site.pageClass.destroy()
            Site.gsapContext?.revert()
            Site.html.classList.remove('scrolled')

            if (Site.cursor && Site.cursor.el.classList.contains('active')) {
                Site.cursor.el.classList.remove('active')
            }

            window.scroll(0, 0)

            data.current.container.remove()
        }

        this.transitions.beforeEnter = async (data) => {
            Site.header.updateHeader(data)

            await this.initPage(data)
        }

        this.transitions.enter = async (data) => {
            await Site.pageClass.awaitCriticalComponents()

            Site.ctx(()=> {
                const tl = gsap.timeline()

                tl.set(Site.html, {
                    cursor: 'auto'
                }, .2)

                tl.add(()=> {
                    Site.scroll.start()
                    Site.html.classList.remove('is-transitioning')
                }, .2)

                tl.to(Site.pageLoader, {
                    autoAlpha: 0,
                    duration: .5,
                    ease: 'power1.inOut'
                }, .2)

                tl.add(()=> {
                    Site.pageClass.pageLoaded()
                    ScrollTrigger.refresh()

                    this.scrollToHash(this.pendingHash)
                    this.pendingHash = null
                }, .3)

                tl.add(() => {
                    const skipLink = document.querySelector('.skip-link')
                    if (skipLink) {
                        skipLink.setAttribute('href', '#' + Site.pageId)
                    }
                    Site.body.setAttribute('tabindex', '-1')
                    Site.body.focus({ preventScroll: true })
                    Site.body.removeAttribute('tabindex')
                }, .35)
            })

            this.setScrollRestoration()
        }

        barba.init({
            sync: true,
            debug: false,
            timeout: 7000,
            transitions: [this.transitions],
        })
    }

    initPage(data) {
        return new Promise(resolve => {
            Site.pageClass && Site.pageClass.destroy()

            let name = 'Default'

            if (data) {
                Site.page = data.next.container
                Site.pageId = data.next.namespace
                Site.pageClass = Site.pageClasses[Site.pageId]
            } else {
                Site.page = document.querySelector('.main')
                Site.pageId = Site.page.getAttribute('id')
            }

            if (Site.pageClasses.hasOwnProperty(Site.pageId)) {
                name = Site.pageClasses[Site.pageId]
            }

            import(`pages/${name}`).then(async (module) => {
                const PageModule = module.default

                Site.gsapContext = gsap.context((self) => {}, Site.page)
                Site.pageClass = new PageModule()
                Site.pageClass.init()

                resolve()
            })
        })
    }

    addEvents() {
        gsap.ticker.add(this.update.bind(this))
        window.addEventListener('resize', this.resize.bind(this))

        if (!Site.isMobile) {
            window.addEventListener('mousemove', this.mouseMove.bind(this))
        }

        // capture phase so we read the hash before barba's XHR drops it
        document.addEventListener('click', this.onLinkClick.bind(this), true)

        Site.scroll.on('scroll', this.scroll.bind(this))
    }

    onLinkClick(e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        const link = e.target.closest('a[href]')

        if (!link || link.target === '_blank') return

        let url

        try {
            url = new URL(link.href, window.location.href)
        } catch (err) {
            return
        }

        if (url.origin !== window.location.origin || !url.hash) return

        if (url.pathname === window.location.pathname) {
            // same page: barba won't transition, so scroll (and sync the url) ourselves
            if (this.scrollToHash(url.hash)) {
                e.preventDefault()
                window.history.pushState(null, '', url.hash)
            }
        } else {
            // cross page: barba navigates but loses the hash — stash it for the enter transition
            this.pendingHash = url.hash
        }
    }

    ctx(fn) {
        if (typeof fn !== 'function') return
        if (Site.gsapContext?.add) {
            Site.gsapContext.add(fn)
        } else {
            fn()
        }
    }

    initCursor() {
        if (!Site.isMobile) {
            Site.mouse = {
                target: null,
                x: 0,
                y: 0,
            }
        }

        if (Site.isMobile) {
            Site.body.querySelector('.cursor').remove()

            return
        }

        Site.cursor = new Cursor()

        this.classes.push(Site.cursor)
    }

    initDrawer() {
        Site.drawer = new Drawer()
        Site.drawer.init()

        this.classes.push(Site.drawer)
    }

    initScroll() {
        Site.scroll = new Lenis({
            easing: t => 1 - Math.pow(1 - t, 5),
            prevent: (node) => {
                return node?.classList?.contains('wp-block-embed')
            }
        })

        Site.scroll.stop()
    }

    scrollToHash(hash) {
        if (!hash) return false

        const id = hash.replace(/^#/, '')

        if (!id) return false

        let target

        try {
            target = Site.page.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id)
        } catch (e) {
            return false
        }

        if (!target) return false

        requestAnimationFrame(() => {
            const offset = Site.isMobile ? remToPx(6) : remToPx(12)

            Site.scroll.scrollTo(target, {
                offset: -offset,
                duration: 2,
                lock: false
            })
        })

        return true
    }

    checkMobile() {
        Site.isTouch = ( 'ontouchstart' in window ) || ( navigator.maxTouchPoints > 0 ) || ( navigator.msMaxTouchPoints > 0 )
        Site.isMobile = window.innerWidth <= 1100
        Site.platform = Site.isMobile ? 'mobile' : 'desktop'

        const className = `is-${Site.platform}`

        if (!Site.html.classList.contains(className)) {
            Site.html.classList.add(className)
        }
    }

    resize() {
        if (Site.isMobile) {
            if (window.innerWidth === Site.windowWidth) {
                return
            } else {
                Site.windowWidth = window.innerWidth
            }
        }

        this.checkMobile()

        if (Site.platformOnLoad !== Site.platform) {
            window.location.reload()
        }

        if (Site.pageClass && Site.pageClass.resize) {
            Site.pageClass.resize()
        }

        this.classes.forEach(instance => {
            instance.resize && instance.resize()
        })

        ScrollTrigger.refresh()
    }

    mouseMove(e) {
        Site.mouse.target = e.target
        Site.mouse.x = e.clientX
        Site.mouse.y = e.clientY
    }

    scroll(scroll) {
        ScrollTrigger.update()

        if (Site.pageClass && Site.pageClass.scroll) {
            Site.pageClass.scroll()
        }

        this.classes.forEach(instance => {
            instance.scroll && instance.scroll()
        })
    }

    update(time) {
        if (Site.scroll && Site.scroll.raf) {
            Site.scroll.raf(time * 1000)
        }

        if (Site.pageClass && Site.pageClass.update) {
            Site.pageClass.update()
        }

        this.classes.forEach(instance => {
            instance.update && instance.update()
        })
    }
}

new App()